import React, { useState, useEffect, useMemo } from 'react';
import { X, ChevronDown, Clock, MessageSquare, Palette } from 'lucide-react';
import { Shift, ShiftSegment, ShiftServiceType, Template, getContrastColor } from './types';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

interface ShiftEditorProps {
    shift: Shift;
    employeeName: string;
    employeeRoleId: string;
    availableTemplates: Template[];
    onSave: (updated: Shift) => void;
    onBatchSave?: (dates: string[], shift: Shift) => void;
    onClose: () => void;
    position: { x: number, y: number };
    currentDate: string;
    absenceTypes: { code: string; isFullDay: boolean; autoApply: boolean; color?: string }[];
}

const ShiftEditor: React.FC<ShiftEditorProps> = ({ 
    shift, employeeName, employeeRoleId, availableTemplates, onSave, onClose, position, currentDate, absenceTypes = [] 
}) => {
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
    const [segments, setSegments] = useState<ShiftSegment[]>([]);

    const effectiveAbsenceTypes = useMemo(() => {
        return Array.isArray(absenceTypes) ? absenceTypes : [];
    }, [absenceTypes]);

    useEffect(() => {
        if (shift && shift.segments && shift.segments.length > 0) {
            setSegments(shift.segments);
            const firstSeg = shift.segments[0];
            if (firstSeg.templateId) setSelectedTemplateId(firstSeg.templateId);
        } else {
            setSegments([{ type: 'horaire', start: '10:00', end: '15:00', note: '' }]);
        }
    }, [shift]);

    const handleTemplateChange = (templateId: string) => {
        setSelectedTemplateId(templateId);
        if (templateId) {
            const tpl = availableTemplates.find(t => t.id === templateId);
            if (tpl) {
                const newSegs = tpl.slots.map(slot => ({
                    type: 'horaire' as const,
                    start: slot.start,
                    end: slot.end,
                    templateId: tpl.id,
                    hasOverride: false,
                    colorOverride: tpl.color,
                    note: ''
                }));
                setSegments(newSegs);
            }
        }
    };

    const handleAddSegment = () => {
        setSegments([...segments, { type: 'horaire', start: '18:00', end: '23:00', note: '' }]);
    };

    const handleRemoveSegment = (index: number) => {
        const newSegs = [...segments];
        newSegs.splice(index, 1);
        setSegments(newSegs);
    };

    const updateSegment = (index: number, field: keyof ShiftSegment, value: any) => {
        const newSegs = [...segments];
        
        let isFullDayAbsence = false;
        // Si on sélectionne un code d'absence "Journée Entière", on remplace tout
        if (field === 'label' && value) {
            const abs = absenceTypes.find(a => a.code === value);
            if (abs?.isFullDay) {
                isFullDayAbsence = true;
                setSegments([{ type: 'code', label: value, note: '' }]);
                return;
            }
        }

        newSegs[index] = { ...newSegs[index], [field]: value, hasOverride: true };

        // Si on change un label d'absence (non full-day)
        if (field === 'label' && value && !isFullDayAbsence && value !== 'REPOS') {
            const abs = absenceTypes.find(a => a.code === value);
            if (abs?.autoApply) {
                newSegs.forEach((seg, i) => {
                    newSegs[i] = { ...seg, label: value, hasOverride: true };
                });
            }
        }

        setSegments(newSegs);
    };

    const handleSave = () => {
        let finalSegments = [...segments];
        let type: 'travail' | 'repos' | 'absence' = 'travail';
        let serviceType: ShiftServiceType = 'none';

        const hasMidi = finalSegments.some(s => s.start && s.start < "16:00");
        const hasSoir = finalSegments.some(s => s.end && (s.end >= "16:00" || s.end < s.start));
        if (hasMidi && hasSoir) serviceType = 'midi+soir';
        else if (hasMidi) serviceType = 'midi';
        else if (hasSoir) serviceType = 'soir';

        // Détection du type global
        if (finalSegments.length === 1) {
            const seg = finalSegments[0];
            if (seg.label === 'REPOS') type = 'repos';
            else if (seg.label && absenceTypes.some(a => a.code === seg.label && a.isFullDay)) type = 'absence';
        }
        
        // Si au moins un segment a un label d'absence flexible
        if (type === 'travail' && finalSegments.some(s => s.label && s.label !== 'REPOS')) {
            // On reste en type travail car il y a des horaires, mais le PDF devra gérer les labels rouges
        }

        onSave({
            ...shift,
            type,
            serviceType,
            segments: finalSegments,
            isManual: true
        });
        onClose();
    };

    // Bulle ancrée à la cellule (brief Planning §4) : 380 px, jamais hors de l'écran.
    const BUBBLE_WIDTH = 380;
    const style: React.CSSProperties = {
        position: 'absolute',
        top: position.y,
        left: Math.max(8, Math.min(position.x, window.innerWidth - BUBBLE_WIDTH - 8)),
        width: BUBBLE_WIDTH,
        zIndex: 100
    };

    const colors = ['#60b4ff', '#c7d0e9', '#ffe39b', '#7fd13b', '#94efe3', '#ff0000'];
    const dateLabel = (() => {
        try { return format(parseISO(currentDate), 'EEEE d MMMM', { locale: fr }); } catch { return currentDate; }
    })();
    const field = 'h-9 border border-border rounded-[9px] bg-surface text-[13px] tabular-nums text-content text-center outline-none focus:border-content focus:shadow-[0_0_0_3px_rgba(15,20,25,.07)]';
    // La carte de code d'absence agit sur le premier segment : un code « journée entière »
    // remplace tout, un code à report automatique se propage aux autres segments (règles inchangées).
    const absenceLabel = segments[0]?.label;

    return (
        <>
            <div className="fixed inset-0 z-[90]" onClick={onClose} aria-hidden="true"></div>
            <div style={style} role="dialog" aria-label={`Créneau de ${employeeName}`} className="bg-surface rounded-[12px] shadow-[0_20px_44px_rgba(15,20,25,.20)] border border-border flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-100 font-sans">
                {/* En-tête : salarié + date */}
                <div className="px-4 py-3 border-b border-border flex justify-between items-start gap-3">
                    <div className="min-w-0">
                        <h4 className="text-[14px] font-semibold text-content truncate">{employeeName}</h4>
                        <p className="text-[12px] text-content-muted capitalize">{dateLabel}</p>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Fermer" className="w-[30px] h-[30px] -mr-1 rounded-lg flex items-center justify-center text-content-muted hover:text-content hover:bg-surface-soft shrink-0"><X size={16} /></button>
                </div>

                <div className="p-4 space-y-4 flex-1 overflow-y-auto max-h-[60vh] text-sm">
                    {/* Modèle horaire */}
                    <div className="relative">
                        <select
                            aria-label="Modèle horaire"
                            className="w-full h-9 border border-border rounded-[9px] pl-3 pr-9 text-[13px] appearance-none bg-surface text-content outline-none focus:border-content"
                            value={selectedTemplateId}
                            onChange={(e) => handleTemplateChange(e.target.value)}
                        >
                            <option value="">Appliquer un modèle horaire…</option>
                            {availableTemplates
                                .filter(t => !employeeRoleId || !t.role || t.role === employeeRoleId)
                                .map(t => (
                                    <option key={t.id} value={t.id}>{t.name}</option>
                                ))}
                        </select>
                        <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-content-muted pointer-events-none" />
                    </div>

                    {/* Segments */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] font-bold uppercase tracking-[.09em] text-content-muted">Segments</span>
                            <button type="button" onClick={handleAddSegment} className="h-7 px-2 rounded-full text-[12px] font-semibold text-content-muted hover:text-content hover:bg-surface-soft whitespace-nowrap shrink-0">+ Ajouter</button>
                        </div>
                        <div className="space-y-2">
                            {segments.map((seg, idx) => (
                                <div key={idx} className="p-2.5 rounded-[10px] border border-border bg-surface-soft/50 space-y-2">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <input type="time" aria-label="Début" className={`${field} flex-1 min-w-[96px]`} value={seg.start || ''} onChange={e => updateSegment(idx, 'start', e.target.value)} />
                                        <span className="text-content-muted">–</span>
                                        <input type="time" aria-label="Fin" className={`${field} flex-1 min-w-[96px]`} value={seg.end || ''} onChange={e => updateSegment(idx, 'end', e.target.value)} />
                                        <div className="relative group/color shrink-0">
                                            <button type="button" title="Couleur du segment" className="w-9 h-9 rounded-[8px] border border-border bg-surface flex items-center justify-center hover:border-content">
                                                <span className="w-4 h-4 rounded-full border border-black/10" style={{ backgroundColor: seg.colorOverride || 'var(--bg-soft)' }} />
                                            </button>
                                            <div className="absolute top-full right-0 mt-1 bg-surface border border-border shadow-2xl rounded-[10px] p-2 grid grid-cols-4 gap-1.5 z-50 hidden group-hover/color:grid group-focus-within/color:grid">
                                                {colors.map(c => (
                                                    <button key={c} type="button" aria-label={c} className="w-6 h-6 rounded-full border border-black/10 hover:scale-110 transition-transform" style={{ backgroundColor: c }} onClick={() => updateSegment(idx, 'colorOverride', c)} />
                                                ))}
                                                <button type="button" title="Couleur du modèle" className="w-6 h-6 rounded-full border border-border bg-surface flex items-center justify-center text-content-muted hover:text-[#8f5450]" onClick={() => updateSegment(idx, 'colorOverride', undefined)}><X size={12} /></button>
                                            </div>
                                        </div>
                                        {segments.length > 1 && (
                                            <button type="button" title="Retirer ce segment" onClick={() => handleRemoveSegment(idx)} className="w-9 h-9 rounded-[8px] flex items-center justify-center text-content-muted hover:text-[#8f5450] hover:bg-state-danger-soft shrink-0"><X size={14} /></button>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 h-8 px-2.5 rounded-[8px] border border-border bg-surface focus-within:border-content">
                                        <MessageSquare size={13} className="text-content-muted shrink-0" />
                                        <input type="text" placeholder="Note pour ce segment…" className="w-full text-[12px] outline-none bg-transparent text-content placeholder:text-content-muted" value={seg.note || ''} onChange={e => updateSegment(idx, 'note', e.target.value)} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Code d'absence */}
                    <div className="p-2.5 rounded-[10px] border border-border bg-surface-soft/50">
                        <div className="text-[10px] font-bold uppercase tracking-[.09em] text-content-muted mb-2">Code d'absence</div>
                        <div className="flex flex-wrap gap-1.5">
                            <button
                                type="button"
                                onClick={() => updateSegment(0, 'label', absenceLabel === 'REPOS' ? undefined : 'REPOS')}
                                className={`h-7 px-2.5 rounded-full text-[11px] font-bold uppercase tracking-wider border whitespace-nowrap shrink-0 transition-all ${absenceLabel === 'REPOS' ? 'bg-black text-white border-black' : 'bg-surface text-content-muted border-border hover:border-content'}`}
                            >
                                Repos
                            </button>
                            {absenceTypes.map(a => {
                                const isSelected = absenceLabel === a.code;
                                const bg = a.color || '#ffe39b';
                                return (
                                    <button
                                        key={a.code}
                                        type="button"
                                        onClick={() => updateSegment(0, 'label', isSelected ? undefined : a.code)}
                                        className={`h-7 px-2.5 rounded-full text-[11px] font-bold uppercase tracking-wider border whitespace-nowrap shrink-0 transition-all ${isSelected ? 'shadow-sm' : 'bg-surface text-content-muted border-border hover:border-content'}`}
                                        style={isSelected ? { backgroundColor: bg, color: getContrastColor(bg), borderColor: 'transparent' } : {}}
                                    >
                                        {a.code}
                                    </button>
                                );
                            })}
                        </div>
                        <p className="text-[11px] text-content-muted mt-2">Un code « journée entière » remplace les horaires du jour ; un code à report automatique s'applique à tous les segments.</p>
                    </div>
                </div>

                {/* Pied : actions à droite, une seule action pleine */}
                <div className="px-4 py-3 border-t border-border bg-surface-soft/60 flex flex-wrap items-center justify-end gap-2">
                    <button type="button" onClick={onClose} className="h-9 px-4 rounded-full border border-border bg-surface text-[13px] font-semibold text-content whitespace-nowrap shrink-0 hover:border-content">Annuler</button>
                    <button type="button" onClick={handleSave} className="h-9 px-5 rounded-full bg-content text-surface text-[13px] font-semibold whitespace-nowrap shrink-0 hover:-translate-y-px transition-transform">Enregistrer</button>
                </div>
            </div>
        </>
    );
};

export default ShiftEditor;
